import { ViewDensity, ViewType } from "@common/types/views/views";
import {
    InvoiceTemplateColumn,
    InvoiceTemplateView,
} from "@common/types/views/invoiceTemplateViews";
import { InvoiceTemplateSort } from "@common/types/invoiceTemplate";
import {
    AMOUNT_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { ViewConfig } from "@lib/views";
import { ActionsCell } from "./Cells/ActionsCell";
import { ArchivedByCell } from "./Cells/ArchivedByCell";
import { ArchivedCell } from "./Cells/ArchivedCell";
import { CreatedByCell } from "./Cells/CreatedByCell";
import { CreatedCell } from "./Cells/CreatedCell";
import { CustomerCell } from "./Cells/CustomerCell";
import { EntityCell } from "./Cells/EntityCell";
import { InvoicesCell } from "./Cells/InvoicesCell";
import { LastUsedCell } from "./Cells/LastUsedCell";
import { NameCell } from "./Cells/NameCell";
import { TotalCell } from "./Cells/TotalCell";
import { UpdatedByCell } from "./Cells/UpdatedByCell";
import { UpdatedCell } from "./Cells/UpdatedCell";
import { InvoiceTemplateRow } from "./invoiceTemplates.types";

export const invoiceTemplatesViewConfig: ViewConfig<
    InvoiceTemplateRow,
    InvoiceTemplateColumn,
    InvoiceTemplateSort
> = {
    columns: {
        [InvoiceTemplateColumn.NAME]: {
            accessorKey: "name",
            name: "Name",
            header: "Name",
            immutable: true,
            cell: NameCell,
            sort: {
                asc: InvoiceTemplateSort.NAME_ASC,
                desc: InvoiceTemplateSort.NAME_DESC,
            },
            minSize: 220,
            maxSize: 600,
        },
        [InvoiceTemplateColumn.CUSTOMER]: {
            accessorKey: "customerName",
            name: "Customer",
            header: "Customer",
            cell: CustomerCell,
            sort: {
                asc: InvoiceTemplateSort.CUSTOMER_ASC,
                desc: InvoiceTemplateSort.CUSTOMER_DESC,
            },
            minSize: 160,
            maxSize: 320,
        },
        [InvoiceTemplateColumn.ENTITY]: {
            accessorKey: "entityId",
            name: "Entity",
            header: "Entity",
            cell: EntityCell,
            sort: {
                asc: InvoiceTemplateSort.ENTITY_ASC,
                desc: InvoiceTemplateSort.ENTITY_DESC,
            },
            minSize: 160,
            maxSize: 280,
        },
        [InvoiceTemplateColumn.TOTAL]: {
            accessorKey: "total",
            name: "Total",
            header: "Total",
            cell: TotalCell,
            align: "right",
            sort: {
                asc: InvoiceTemplateSort.TOTAL_ASC,
                desc: InvoiceTemplateSort.TOTAL_DESC,
            },
            ...AMOUNT_COLUMN_SIZE,
        },
        [InvoiceTemplateColumn.INVOICES]: {
            accessorKey: "sourcedInvoiceCount",
            name: "Invoices",
            header: "Invoices",
            cell: InvoicesCell,
            sort: {
                asc: InvoiceTemplateSort.INVOICES_ASC,
                desc: InvoiceTemplateSort.INVOICES_DESC,
            },
            minSize: 100,
            maxSize: 160,
        },
        [InvoiceTemplateColumn.LAST_USED]: {
            accessorKey: "lastUsedAt",
            name: "Last used",
            header: "Last used",
            cell: LastUsedCell,
            sort: {
                asc: InvoiceTemplateSort.LAST_USED_AT_ASC,
                desc: InvoiceTemplateSort.LAST_USED_AT_DESC,
            },
            ...DATE_COLUMN_SIZE,
        },
        [InvoiceTemplateColumn.CREATED]: {
            accessorKey: "createdAt",
            name: "Created on",
            header: "Created on",
            cell: CreatedCell,
            sort: {
                asc: InvoiceTemplateSort.CREATED_AT_ASC,
                desc: InvoiceTemplateSort.CREATED_AT_DESC,
            },
            ...DATE_COLUMN_SIZE,
        },
        [InvoiceTemplateColumn.CREATED_BY]: {
            accessorKey: "createdByUserId",
            name: "Created by",
            header: "Created by",
            cell: CreatedByCell,
            minSize: 160,
            maxSize: 240,
        },
        [InvoiceTemplateColumn.UPDATED]: {
            accessorKey: "updatedAt",
            name: "Updated on",
            header: "Updated on",
            cell: UpdatedCell,
            sort: {
                asc: InvoiceTemplateSort.UPDATED_AT_ASC,
                desc: InvoiceTemplateSort.UPDATED_AT_DESC,
            },
            ...DATE_COLUMN_SIZE,
        },
        [InvoiceTemplateColumn.UPDATED_BY]: {
            accessorKey: "updatedByUserId",
            name: "Updated by",
            header: "Updated by",
            cell: UpdatedByCell,
            minSize: 160,
            maxSize: 240,
        },
        [InvoiceTemplateColumn.ARCHIVED]: {
            accessorKey: "archivedAt",
            name: "Archived on",
            header: "Archived on",
            cell: ArchivedCell,
            sort: {
                asc: InvoiceTemplateSort.ARCHIVED_AT_ASC,
                desc: InvoiceTemplateSort.ARCHIVED_AT_DESC,
            },
            ...DATE_COLUMN_SIZE,
        },
        [InvoiceTemplateColumn.ARCHIVED_BY]: {
            accessorKey: "archivedByUserId",
            name: "Archived by",
            header: "Archived by",
            cell: ArchivedByCell,
            minSize: 160,
            maxSize: 240,
        },
        [InvoiceTemplateColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            immutable: true,
            hiddenFromSettings: true,
            cell: ActionsCell,
            minSize: 180,
            maxSize: 180,
        },
    },
};

export const defaultInvoiceTemplateView: InvoiceTemplateView = {
    type: ViewType.INVOICE_TEMPLATE,
    columns: [
        { id: InvoiceTemplateColumn.NAME, options: { pin: "left" } },
        { id: InvoiceTemplateColumn.CUSTOMER },
        { id: InvoiceTemplateColumn.ENTITY },
        { id: InvoiceTemplateColumn.TOTAL },
        { id: InvoiceTemplateColumn.INVOICES },
        { id: InvoiceTemplateColumn.LAST_USED },
        { id: InvoiceTemplateColumn.CREATED },
        { id: InvoiceTemplateColumn.CREATED_BY },
        { id: InvoiceTemplateColumn.UPDATED },
        { id: InvoiceTemplateColumn.UPDATED_BY },
        { id: InvoiceTemplateColumn.ARCHIVED },
        { id: InvoiceTemplateColumn.ARCHIVED_BY },
        { id: InvoiceTemplateColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: { density: ViewDensity.MD },
};
